import { buildApiUrl, createHeaders } from "./apiClient";

const getCurrentUserId = () => {
  // 1. Direct userId key (used across the app)
  const direct =
    localStorage.getItem("userId") || sessionStorage.getItem("userId");
  if (direct && !Number.isNaN(Number(direct))) return Number(direct);

  // 2. Fall back to userprofile blob
  try {
    const raw =
      localStorage.getItem("userprofile") ||
      sessionStorage.getItem("userprofile");
    if (raw) {
      const u = JSON.parse(raw);
      return u?.id || u?.user_id || u?.userId || null;
    }
  } catch {
    /* ignore */
  }
  return null;
};

/**
 * Map a notification's (entity_type, entity_id) to a frontend route.
 * Centralised here so we never store URLs in the DB.
 *
 * Entity types emitted today (see biri_flask notifications):
 *   ask_expert_question, podcast_episode, reverse_seminar_case,
 *   connection_request, conference_gwbi, winery, event.
 */
export const linkForNotification = (n) => {
  if (!n) return "/notifications";
  const id = n.entity_id;
  switch (n.entity_type) {
    case "ask_expert_question":
      return id != null ? `/tool/ask-expert/${id}` : "/tool/ask-expert";
    case "reverse_seminar_case":
      return id != null
        ? `/tool/reverse-seminars?id=${id}`
        : "/tool/reverse-seminars";
    case "podcast_episode":
      // No per-episode detail page yet — land on the podcasts index.
      return "/podcasts";
    case "connection_request":
      return "/connections";
    case "dm":
    case "message":
      return "/inbox";
    case "discussion":
    case "discussion_reply":
      return id != null ? `/discussions?id=${id}` : "/discussions";
    case "conference_gwbi":
    case "event":
      return id != null ? `/events/${id}` : "/events";
    case "winery":
      return id != null ? `/wineries/${id}` : "/wineriestable";
    case "survey":
      return id != null ? `/survey/${id}` : "/dashboard";
    case "post":
      return "/dashboard";
    case "news":
    case "conference":
    case "publication":
    case "video":
      return "/dashboard";
    default:
      return "/notifications";
  }
};

export const fetchNotifications = async ({
  unreadOnly = false,
  limit = 25,
  offset = 0,
} = {}) => {
  const userId = getCurrentUserId();
  const params = new URLSearchParams();
  if (unreadOnly) params.set("unread_only", "1");
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const res = await fetch(buildApiUrl(`/notifications?${params.toString()}`), {
    method: "GET",
    headers: createHeaders({ userId }),
  });
  if (!res.ok) throw new Error(`Failed to load notifications (${res.status})`);
  return res.json();
};

export const fetchUnreadCount = async () => {
  const userId = getCurrentUserId();
  const res = await fetch(buildApiUrl("/notifications/unread-count"), {
    method: "GET",
    headers: createHeaders({ userId }),
  });
  if (!res.ok) return { count: 0 };
  return res.json();
};

export const markRead = async (notificationId) => {
  const userId = getCurrentUserId();
  const res = await fetch(
    buildApiUrl(`/notifications/${notificationId}/read`),
    { method: "POST", headers: createHeaders({ userId }) },
  );
  if (!res.ok) throw new Error("Failed to mark notification read");
  return res.json();
};

export const markAllRead = async () => {
  const userId = getCurrentUserId();
  const res = await fetch(buildApiUrl("/notifications/read-all"), {
    method: "POST",
    headers: createHeaders({ userId }),
  });
  if (!res.ok) throw new Error("Failed to mark all read");
  return res.json();
};
